<template>
  <aside class="w-96 flex-none border-l border-gray-200 overflow-y-auto relative">
    <!-- CLOSE. The panel used to have none and no v-if either, so it was
         permanently mounted and permanently 384px wide — there was no way back
         to a full-width table. Sticky so it stays reachable while scrolling a
         long verdict. -->
    <button
      v-if="ticket || pullRequest"
      type="button"
      class="sticky top-0 float-right z-[2] w-7 h-7 -mr-1 mt-1 rounded
             text-gray-600 hover:bg-gray-50 hover:text-gray-900 bg-white"
      title="Close panel (Esc)"
      @click="$emit('close')"
    >&times;</button>

    <p v-if="!ticket && !pullRequest" class="px-5 py-6 text-[13px] text-gray-600">
      Select a row to read the verdict and the evidence behind it.
    </p>

    <div v-else-if="ticket" class="p-5">
      <div class="text-xs text-gray-600 font-mono pr-7 flex items-center gap-1.5">
        <span>
          #{{ ticket.requirement }} · {{ ticket.requirement_type }} ·
          {{ label(STATUS_LABELS, ticket.requirement_status) }}
        </span>
        <!-- Real <a href>, resolved via $router rather than hand-built, so
             cmd-click / middle-click / copy-link all work like the user
             expects. New tab: a same-tab nav would throw away the filtered
             board this deeplink is triaged from. ticket.requirement, never
             ticket.id — the latter is the HadesTicket id and is null on every
             un-investigated row. -->
        <a
          :href="workLink"
          target="_blank"
          rel="noopener"
          class="text-primary-700 hover:text-primary-800 shrink-0"
          title="Open in the Work list"
        ><font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" /></a>
      </div>
      <h3 class="sensitive text-base font-semibold text-gray-900 leading-snug mt-1.5" data-demo-blur>
        {{ ticket.requirement_title }}
      </h3>

      <p v-if="!ticket.investigated" class="mt-4 text-[13px] text-gray-600">
        Hades has not investigated this requirement yet, so there is no verdict.
        It will be picked up by the next run.
      </p>

      <p v-else class="sensitive mt-4 text-[13px] text-gray-800 leading-relaxed" data-demo-blur>{{ ticket.story }}</p>

      <!-- An Unknown verdict carries NO recommendation by design: the agent
           could not establish the state, and proposing an action on that basis
           is what this system exists to stop. -->
      <div
        v-if="isUnknown"
        class="mt-4 rounded-lg border border-red-light bg-red-tint p-3
               text-xs text-red-dark leading-relaxed"
      >
        Evidence was insufficient to determine this requirement's state, so
        there is no recommended action.
      </div>
      <div
        v-else-if="ticket.recommended_action"
        class="mt-4 rounded-lg border border-secondary-200 bg-secondary-100 p-3
               text-[13px] text-gray-800 leading-relaxed"
      >
        <span class="font-semibold text-gray-900">Recommended</span> · <span class="sensitive" data-demo-blur>{{ ticket.recommended_action }}</span>
      </div>

      <!-- THE REPLY HADES WROTE. Present only when the ticket was past its
           priority reply window that night. Posted through the comments API
           after the verdict was stored, so "posted" means the requester was
           emailed (web) or the owner saw it in the thread (internal note);
           "not posted" means the draft is here and nowhere else. -->
      <div
        v-if="ticket.reply"
        class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3
               text-[13px] text-gray-800 leading-relaxed"
      >
        <div class="flex items-center gap-2 text-xs mb-1.5">
          <span class="font-semibold text-gray-900">
            {{ ticket.reply.kind === 'update' ? 'Update' : 'Hold' }} reply
          </span>
          <span class="text-gray-500">
            · to {{ ticket.reply.audience === 'customer' ? 'the customer' : 'the team' }}
            · as {{ ticket.reply.source === 'web' ? 'customer-visible comment' : 'internal note' }}
          </span>
          <span
            class="ml-auto"
            :class="ticket.reply_comment ? 'text-secondary-700' : 'text-red-dark font-medium'"
          >{{ ticket.reply_comment ? `posted · comment #${ticket.reply_comment}` : 'not posted' }}</span>
        </div>
        <p class="whitespace-pre-line sensitive" data-demo-blur>{{ ticket.reply.text }}</p>
        <p v-if="ticket.reply.next_update_by" class="mt-1.5 text-xs text-gray-500">
          Next update by {{ ticket.reply.next_update_by }}
        </p>
      </div>

      <template v-if="ticket.investigated">
      <h4 class="text-[13px] font-semibold text-gray-900 mt-5 mb-2">Verdict</h4>
      <table class="w-full">
        <tr v-for="fact in facts" :key="fact.label" class="border-b border-gray-200">
          <td class="py-1.5 text-xs text-gray-600">{{ fact.label }}</td>
          <td
            class="py-1.5 text-xs text-gray-800 text-right font-medium"
            :class="{ sensitive: fact.sensitive }"
          >
            {{ fact.value }}
          </td>
        </tr>
      </table>

      <h4 class="text-[13px] font-semibold text-gray-900 mt-5 mb-2">Evidence</h4>
      <p v-if="!ticket.citations || !ticket.citations.length" class="text-xs text-gray-600">
        No typed evidence was recorded for this verdict.
      </p>
      <div v-for="(cite, i) in ticket.citations" :key="i" class="mb-3">
        <div class="font-mono text-xs text-gray-800">
          {{ cite.namespace }}:{{ cite.id }}
        </div>
        <div class="sensitive text-xs text-gray-600 mt-0.5 leading-relaxed" data-demo-blur>
          {{ cite.why_it_matters }}
        </div>
      </div>

      <p v-if="ticket.record_changed_since_investigation" class="mt-3 text-xs text-gray-600 leading-relaxed">
        This requirement changed after Hades investigated it — the verdict may
        describe a record that has since moved.
      </p>

      </template>

      <!-- ON EVERY REQUIREMENT. This was gated to shipped_record_stale, the one
           queue with a cited PR behind it — which meant the board could close 9
           of 404 rows and every other requirement was read-only. A human
           clicking is the authorization; the verdict only decides what the
           internal note can cite. isCloseable now only chooses the emphasis. -->
      <div class="mt-5">
        <!-- CLOSE-AS. Defaulted from the verdict's queues (irrelevant -> closed,
             everything else -> done) but rendered as a real control, not a
             hidden decision — a default that can't be seen is worse than no
             default. closeStatus is a data field, not a computed straight off
             defaultCloseStatus, so picking a value here survives while the
             confirmation modal is open and isn't silently overwritten by a
             re-render. -->
        <label class="flex items-center gap-2 mb-2 text-[11px] text-gray-600">
          Close as
          <!-- :value/@change, NOT v-model — the watcher below needs to tell a
               MANUAL pick apart from its own re-default, and v-model gives
               both the same event. -->
          <select
            :value="closeStatus"
            class="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white
                   text-gray-800"
            @change="setCloseStatus($event.target.value)"
          >
            <option v-for="s in CLOSE_STATUS_OPTIONS" :key="s" :value="s">
              {{ label(STATUS_LABELS, s) }}
            </option>
          </select>
        </label>
        <button
          type="button"
          class="w-full h-[38px] rounded text-[13px] font-semibold"
          :class="isCloseable
            ? 'bg-primary-500 text-white hover:bg-primary-600'
            : 'border border-gray-300 text-gray-800 hover:bg-gray-50'"
          @click="pendingClose = true"
        >
          Close requirement #{{ ticket.requirement }}
        </button>
        <p class="mt-2 text-[11px] text-gray-600">
          {{ closeHint }}
        </p>
      </div>
    </div>

    <div v-else class="p-5">
      <a
        :href="pullRequest.url"
        target="_blank"
        rel="noopener"
        class="font-mono text-xs text-primary-700 hover:underline"
      >{{ pullRequest.repository }}#{{ pullRequest.pr_number }}</a>
      <h3 class="sensitive text-base font-semibold text-gray-900 leading-snug mt-1.5" data-demo-blur>
        {{ pullRequest.title }}
      </h3>

      <p class="sensitive mt-4 text-[13px] text-gray-800 leading-relaxed" data-demo-blur>{{ pullRequest.summary }}</p>

      <table class="w-full mt-4">
        <tr v-for="fact in prFacts" :key="fact.label" class="border-b border-gray-200">
          <td class="py-1.5 text-xs text-gray-600">{{ fact.label }}</td>
          <td class="py-1.5 text-xs text-gray-800 text-right font-medium">
            {{ fact.value }}
          </td>
        </tr>
      </table>

      <p
        v-if="pullRequest.strategic_rationale"
        class="sensitive mt-4 text-[13px] text-gray-800 leading-relaxed"
        data-demo-blur
      >
        {{ pullRequest.strategic_rationale }}
      </p>
    </div>

    <ConfirmationModal
      :is-visible="pendingClose"
      :message="closeMessage"
      @confirm="doClose"
      @cancel="pendingClose = false"
    />
  </aside>
</template>

<script>
import axios from 'axios';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import {
  DONE_STATUSES, SELECTABLE_STATUSES, STATUS_LABELS, formatDateValue,
} from '../support_constants';
import {
  ATTENTION_LABELS, AXIS_LABELS, CATEGORY_LABELS, CLOSEABLE_QUEUES,
  DELIVERY_LABELS, LINKAGE_LABELS, QUEUE_LABELS, label,
} from './hades_constants';
import { isPersonCreator } from './hades_filters';
import { isDemoMode } from '@/utils/demoSpoof';

// The panel's two-item choice, DERIVED from the shared vocabulary rather than
// hand-written a second time — SELECTABLE_STATUSES ∩ DONE_STATUSES is exactly
// {done, closed}. 'not_relevant' is a real DONE_STATUSES member and arguably
// the truest match for "N/A", but it is deliberately absent from
// SELECTABLE_STATUSES and no picker in the app offers it; adding it here would
// make Hades the only surface that can assign a status nothing else can.
export const CLOSE_STATUS_OPTIONS = SELECTABLE_STATUSES.filter((s) => DONE_STATUSES.has(s));

// 'irrelevant' is config.yaml's own definition of "N/A, stale" (duplicate /
// superseded / withdrawn / erroneous_intake / unsupported_or_out_of_scope /
// unintelligible) — the exact case Irene wants routed to 'closed'. Every other
// queue defaults to 'done', which is already today's behaviour for the
// completed-work case (the endpoint's own default when no status is sent).
export function defaultCloseStatus(ticket) {
  return (ticket?.queues || []).includes('irrelevant') ? 'closed' : 'done';
}

export default {
  name: 'Hades_Detail',
  components: { ConfirmationModal },
  props: {
    ticket: { type: Object, default: null },
    pullRequest: { type: Object, default: null },
  },
  // 'closed' = a requirement was closed in the DB (triggers a refetch).
  // 'close'  = dismiss this panel. Two different things, one letter apart —
  //            named this way because the parent already listened for 'closed'.
  emits: ['closed', 'close'],
  data() {
    // Exposed for the template, which renders a status label in the header.
    return {
      pendingClose: false,
      STATUS_LABELS,
      CLOSE_STATUS_OPTIONS,
      closeStatus: defaultCloseStatus(this.ticket),
      // Set ONLY by setCloseStatus(), on a manual pick. Lets the watcher
      // below tell "the user chose this" from "this is still the default" —
      // see the watcher's own comment for why that distinction is the fix.
      closeStatusTouched: false,
    };
  },
  computed: {
    workLink() {
      if (!this.ticket) return '';
      return this.$router.resolve({
        name: 'Support-WorkDetail',
        params: { selectedId: this.ticket.requirement },
      }).href;
    },
    isUnknown() {
      return (this.ticket?.queues || []).includes('needs_evidence');
    },
    isCloseable() {
      return (this.ticket?.queues || []).some((q) => CLOSEABLE_QUEUES.includes(q));
    },
    facts() {
      const t = this.ticket;
      if (!t) return [];
      // Two axes are enumerations with their own English; the rest are already
      // written as prose by the fleet. AXIS_MAPS keeps that from being a chain
      // of ternaries that grows one link per labelled axis.
      const AXIS_MAPS = { delivery: DELIVERY_LABELS, attention: ATTENTION_LABELS };
      return [
        { label: 'Queue', value: (t.queues || []).map((q) => label(QUEUE_LABELS, q)).join(', ') },
        ...Object.keys(AXIS_LABELS).map((axis) => ({
          label: AXIS_LABELS[axis],
          value: AXIS_MAPS[axis] ? label(AXIS_MAPS[axis], t[axis]) : t[axis],
        })),
        // `sensitive` tags the value cell for Demo Mode.
        { label: 'Tenant', value: t.tenant_name || '—', sensitive: !!t.tenant_name },
        // 'Unowned' matches the word the list uses for the same state. Two
        // words for one condition reads as two different conditions.
        { label: 'Owner', value: t.owner_name || 'Unowned' },
        { label: 'Created by', value: t.creator_name || '—',
          sensitive: isPersonCreator(t.creator_name) },
        { label: 'Created', value: this.createdFact(t.requirement_created_at) },
        { label: 'Last reply', value: this.lastReplyFact(t) },
        // A bare calendar day, so it renders through formatDateValue with no
        // timeZone option — toLocalDate() applies no conversion to one, which
        // is what keeps a date due Sep 3 from reading as Sep 2 west of UTC.
        { label: 'Due', value: t.requirement_due_date
          ? formatDateValue(t.requirement_due_date) : '—' },
      ];
    },
    prFacts() {
      const p = this.pullRequest;
      if (!p) return [];
      const reqs = p.requirement_ids || [];
      return [
        { label: 'Category', value: label(CATEGORY_LABELS, p.primary_category) },
        { label: 'Delivery', value: label(DELIVERY_LABELS, p.delivery_state) },
        { label: 'Customer link', value: label(LINKAGE_LABELS, p.customer_linkage) },
        { label: 'Engineer', value: p.engineer_id || p.author_login || '—' },
        { label: 'Requirements', value: reqs.length ? reqs.map((i) => `#${i}`).join(', ') : 'None' },
      ];
    },
    closeHint() {
      if (!this.ticket?.investigated) {
        return 'Hades has not investigated this requirement — no evidence will '
          + 'be cited.';
      }
      return (this.ticket.citations || []).length
        ? 'Posts the cited evidence as an internal note.'
        : 'Posts the verdict as an internal note. No evidence was cited.';
    },
    closeMessage() {
      const t = this.ticket;
      if (!t) return '';
      const cites = (t.citations || []).map((c) => `${c.namespace}:${c.id}`).join(', ');
      const statusLine = `Status: ${label(STATUS_LABELS, this.closeStatus)}`;
      // ConfirmationModal renders this in an untagged <p>, so the title (free
      // text that can name a customer) is left out while Demo Mode is on.
      const title = isDemoMode() ? '' : ` — ${t.requirement_title}`;
      const head = [`#${t.requirement}${title}`, statusLine, ''];
      if (!t.investigated) {
        return head.concat([
          'Hades has not investigated this requirement, so there is no verdict.',
          '',
          'Closing will post a note recording that it was closed from the board.',
        ]).join('\n');
      }
      return head.concat([
        `Hades found this ${label(DELIVERY_LABELS, t.delivery)}.`,
        cites ? `Evidence: ${cites}` : 'No typed evidence recorded.',
        '',
        'The verdict will be posted as an internal note on the requirement.',
      ]).join('\n');
    },
  },
  watch: {
    // WATCHES `ticket` ITSELF (object identity), not `ticket.requirement`.
    // Support_Hades.vue polls the whole portfolio every 30s while a run is in
    // flight and replaces `tickets` wholesale, so `selectedTicket` — and
    // therefore this prop — gets a NEW object with the SAME `requirement` on
    // every poll. A watch on `.requirement` never fires for that, so a row
    // re-verdicted mid-poll (dormant -> irrelevant, say) kept whatever
    // default was picked before the verdict changed. This watcher fires on
    // every poll tick instead and decides for itself whether that tick
    // changed anything worth re-defaulting for.
    ticket(next, prev) {
      // MID-DECISION IS NOT THE MOMENT TO MOVE IT. The confirmation modal
      // already has closeStatus's value on screen; changing it under an open
      // confirmation means the reader read one thing and would confirm
      // another.
      if (this.pendingClose) return;
      if (next?.requirement !== prev?.requirement) {
        // An actual row change is a fresh decision — re-default AND drop
        // whatever the previous row's manual pick (if any) was, or it would
        // silently carry over as this row's "touched" state despite never
        // having been touched.
        this.closeStatus = defaultCloseStatus(next);
        this.closeStatusTouched = false;
        return;
      }
      // Same row, new object (a poll tick). Re-default only if the user has
      // not made a manual pick — that pick must survive a poll landing while
      // they are still deciding. An untouched default keeps tracking the
      // verdict, since it is not a decision yet, just a suggestion.
      if (!this.closeStatusTouched) {
        this.closeStatus = defaultCloseStatus(next);
      }
    },
  },
  methods: {
    label,
    setCloseStatus(value) {
      this.closeStatus = value;
      this.closeStatusTouched = true;
    },
    // Full date plus age. The panel has room for the long form, unlike the
    // list's narrow column. UTC to match how the timestamp is stored — in local
    // time a 03:21 UTC filing shows as the previous day west of Greenwich.
    createdFact(iso) {
      if (!iso) return '—';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '—';
      const shown = date.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
      });
      const days = Math.floor((Date.now() - date.getTime()) / 86400000);
      if (days < 1) return `${shown} (today)`;
      return `${shown} (${days} day${days === 1 ? '' : 's'} ago)`;
    },
    // Client-filed tickets owe the next Kairon reply inside the priority ladder
    // (p0 same day, p1 next business day, p2 five, p3/none ten), counted from
    // our last reply or from filing. Internal tickets owe nothing, so the row
    // says so rather than showing a due date.
    lastReplyFact(t) {
      if (!t.customer_submitted) return 'n/a (internal)';
      const due = new Date(t.response_due_at);
      const shown = due.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
      });
      const overdue = due.getTime() < Date.now();
      const next = overdue ? `next was due ${shown}` : `next due ${shown}`;
      const waiting = t.awaiting_reply
        ? ` — customer replied ${this.createdFact(t.last_customer_comment_at)}, unanswered` : '';
      if (t.last_response_at) return `${this.createdFact(t.last_response_at)} — ${next}${waiting}`;
      return `none — ${next}${waiting}`;
    },
    async doClose() {
      this.pendingClose = false;
      try {
        // requirement_id when there is no verdict (id is null on an
        // un-investigated row); hades_ticket_id when there is, so the note can
        // cite it. status alongside either branch — the endpoint defaults to
        // 'done' when it's absent, which is why every close used to land there
        // regardless of the picker below.
        await axios.post('/api/hades/close/', this.ticket.id
          ? { hades_ticket_id: this.ticket.id, status: this.closeStatus }
          : { requirement_id: this.ticket.requirement, status: this.closeStatus });
        this.$emit('closed');
      } catch (e) {
        // 409 is the stale-read guard — the requirement moved after Hades looked
        // at it. Surface it; the verdict needs refreshing, not forcing.
        window.alert(e.response?.data?.detail || 'Could not close the requirement.');
      }
    },
  },
};
</script>
